import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bike, LoaderCircle, RefreshCw, ArrowLeft, PackageCheck, Wallet } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { useAuth, formatApiError } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const KIND_STYLE = {
  food: "bg-[#F97316]/10 text-[#F97316]",
  shop: "bg-[#7C3AED]/15 text-[#A78BFA]",
};

export default function RiderMode() {
  const { user, checked } = useAuth();
  const [tab, setTab] = useState("available");
  const [available, setAvailable] = useState(null);
  const [mine, setMine] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [podFor, setPodFor] = useState(null);
  const podFileRef = useRef(null);
  const watchRef = useRef(null);

  // Live GPS sharing — customers see the rider on their order tracking map
  const toggleSharing = () => {
    if (sharing) {
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
      setSharing(false);
      return;
    }
    if (!navigator.geolocation) return toast.error("Geolocation not available on this device.");
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        axios.post(`${API}/rider/location`, { lat: pos.coords.latitude, lng: pos.coords.longitude }, headers()).catch(() => {});
      },
      () => toast.error("Location permission denied."),
      { enableHighAccuracy: true, maximumAge: 15000 }
    );
    setSharing(true);
    toast.success("Live location ON — customers can track you 🛵");
  };

  useEffect(() => () => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
  }, []);

  // Proof-of-delivery: pick a photo, upload, then complete with pod_url
  const attachPod = async (file, kind, orderId) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setBusyId(orderId);
      try {
        const base64 = String(reader.result).split(",")[1];
        const { data } = await axios.post(`${API}/chat/files`, { data_base64: base64, content_type: file.type, folder: "pod" }, headers());
        act("complete", kind, orderId, "Delivered!", data.url);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        setBusyId(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const load = useCallback(async () => {
    try {
      const [a, m] = await Promise.all([
        axios.get(`${API}/rider/available`, headers()),
        axios.get(`${API}/rider/mine`, headers()),
      ]);
      setAvailable(a.data.deliveries);
      setMine(m.data);
    } catch {
      setAvailable([]);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user, load]);

  const act = async (path, kind, orderId, successMsg, podUrl = "") => {
    setBusyId(orderId);
    try {
      const { data } = await axios.post(`${API}/rider/${path}`, { kind, order_id: orderId, pod_url: podUrl }, headers());
      toast.success(data.earned ? `${successMsg} +₹${data.earned} earned 🎉` : successMsg);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090B]">
        <LoaderCircle className="h-8 w-8 animate-spin text-[#06B6D4]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const DeliveryCard = ({ d, action }) => (
    <div data-testid={`rider-delivery-${d.id.slice(-6)}`} className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-jbmono text-sm font-bold text-[#06B6D4]">#{d.id.slice(-6)}</span>
        <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider ${KIND_STYLE[d.kind]}`}>
          {d.kind === "food" ? "🍔 Food" : "🛍️ Shop"}
        </span>
        <span className="rounded-full bg-[#22C55E]/10 px-3 py-1 font-jbmono text-[10px] font-bold text-[#22C55E]">+₹{d.payout}</span>
        <span className="ml-auto font-jbmono text-[11px] text-slate-500">{String(d.created_at).slice(0, 16).replace("T", " ")}</span>
      </div>
      <p className="mt-3 text-sm font-semibold">
        {d.items.map((i) => `${i.qty}× ${i.name}`).join(" · ")}
        <span className="ml-2 font-jbmono text-[#22C55E]">₹{d.total}</span>
      </p>
      <p className="mt-1.5 font-jbmono text-[11px] text-slate-500">📍 {d.address} · {d.phone}</p>
      {action}
    </div>
  );

  return (
    <div data-testid="rider-mode" className="min-h-screen bg-[#09090B] text-white">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#09090B]/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5 sm:px-6">
          <Link to="/" data-testid="rider-logo" className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-display text-base font-extrabold tracking-tight">
              FUN<span className="text-[#06B6D4]">Z</span>
              <span className="ml-2 rounded-full bg-[#06B6D4]/15 px-2.5 py-0.5 font-jbmono text-[9px] uppercase tracking-widest text-[#06B6D4]">
                Rider Hub
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-2.5">
            <button
              data-testid="rider-refresh"
              onClick={load}
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-[#06B6D4]/60 hover:text-white"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <Link to="/app" data-testid="rider-back" className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-white">
              <ArrowLeft className="h-3.5 w-3.5" /> My FUNZ
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <h1 className="flex items-center gap-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          <Bike className="h-8 w-8 text-[#06B6D4]" /> Rider Dispatch
        </h1>
        <p className="mt-2 text-sm text-slate-400">Food orders out-for-delivery and shipped shop orders land here. Refreshes every 15 seconds.</p>

        <div className="mt-8 grid grid-cols-3 gap-3">
          {[
            { label: "Active deliveries", value: mine?.active.length ?? "…", icon: PackageCheck },
            { label: "Earned today", value: mine ? `₹${mine.earnings.today}` : "…", icon: Wallet },
            { label: "Total earned", value: mine ? `₹${mine.earnings.total}` : "…", icon: Wallet },
          ].map((s, i) => (
            <div key={s.label} data-testid={`rider-stat-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
              <p className="font-display text-2xl font-extrabold">{s.value}</p>
              <p className="mt-1 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="rider-tabs">
          {[
            { id: "available", label: `🛵 Available (${available?.length ?? 0})` },
            { id: "mine", label: `📦 My Deliveries (${(mine?.active.length ?? 0) + (mine?.completed.length ?? 0)})` },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`rider-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === t.id ? "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white" : "border border-white/10 text-slate-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
          <button
            data-testid="rider-share-location"
            onClick={toggleSharing}
            className={`ml-auto flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
              sharing ? "bg-[#22C55E] text-[#09090B]" : "border border-[#22C55E]/40 text-[#22C55E] hover:bg-[#22C55E]/10"
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${sharing ? "animate-pulse bg-[#09090B]" : "bg-[#22C55E]"}`} />
            {sharing ? "Live" : "Share live location"}
          </button>
          <input
            ref={podFileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid="rider-pod-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f && podFor) attachPod(f, podFor.kind, podFor.id);
              e.target.value = "";
            }}
          />
        </div>

        <div className="mt-8 space-y-4">
          {tab === "available" ? (
            available === null ? (
              <LoaderCircle className="h-6 w-6 animate-spin text-[#06B6D4]" />
            ) : available.length === 0 ? (
              <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-sm text-slate-500">
                No deliveries waiting — new ones ping here the moment chefs and vendors hand off 🛵
              </p>
            ) : (
              available.map((d) => (
                <DeliveryCard
                  key={d.id}
                  d={d}
                  action={
                    <button
                      data-testid={`rider-accept-${d.id.slice(-6)}`}
                      onClick={() => act("accept", d.kind, d.id, "Delivery accepted — you're on the way!")}
                      disabled={busyId === d.id}
                      className="mt-4 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
                    >
                      {busyId === d.id ? "…" : "Accept Delivery"}
                    </button>
                  }
                />
              ))
            )
          ) : mine === null ? (
            <LoaderCircle className="h-6 w-6 animate-spin text-[#06B6D4]" />
          ) : (
            <>
              {mine.active.map((d) => (
                <DeliveryCard
                  key={d.id}
                  d={d}
                  action={
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        data-testid={`rider-complete-${d.id.slice(-6)}`}
                        onClick={() => act("complete", d.kind, d.id, "Delivered!")}
                        disabled={busyId === d.id}
                        className="rounded-full bg-gradient-to-r from-[#06B6D4] to-[#22C55E] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50"
                      >
                        {busyId === d.id ? "…" : "Mark Delivered"}
                      </button>
                      <button
                        data-testid={`rider-pod-${d.id.slice(-6)}`}
                        onClick={() => { setPodFor({ kind: d.kind, id: d.id }); setTimeout(() => podFileRef.current?.click(), 50); }}
                        disabled={busyId === d.id}
                        className="rounded-full border border-[#06B6D4]/40 px-4 py-2.5 font-jbmono text-[10px] font-bold text-[#06B6D4] hover:bg-[#06B6D4]/10 disabled:opacity-50"
                      >
                        📸 Photo + Deliver
                      </button>
                    </div>
                  }
                />
              ))}
              {mine.completed.length > 0 && (
                <p className="pt-2 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Completed</p>
              )}
              {mine.completed.map((d) => (
                <div key={d.id} data-testid={`rider-done-${d.order_id.slice(-6)}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase ${KIND_STYLE[d.kind]}`}>{d.kind}</span>
                  <span className="font-jbmono text-xs text-slate-400">#{d.order_id.slice(-6)}</span>
                  <span className="font-jbmono text-[10px] text-slate-600">{String(d.completed_at).slice(0, 16).replace("T", " ")}</span>
                  <span className="ml-auto font-jbmono text-xs font-bold text-[#06B6D4]">+₹{d.amount}</span>
                </div>
              ))}
              {!mine.active.length && !mine.completed.length && (
                <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-sm text-slate-500">
                  Nothing yet — grab a delivery from the Available tab.
                </p>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
